import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Row} from '../Layout';
import {RectangularSkeleton} from '../Skeletons';
import {registerGlobalStyle} from '../../theme';
import {HoopsPropTypes} from '../utils';
import classNames from 'classnames';
import {
  ChevronLeft as ScrollLeftIcon,
  ChevronRight as ScrollRightIcon,
  DragIndicator
} from '@mui/icons-material';
import {ImageThumbnail} from './ImageThumbnail';
import {ImagePreview} from './ImagePreview';
import {MultiRowDragDrop} from '../DragDrop';
import {HoverTools} from './HoverTools';
import {ImageGalleryOverlay} from './ImageGalleryOverlay';

registerGlobalStyle('.image-gallery', (theme) => ({
  width: '100%',
  height: '100%',
  '.image-container': {
    position: 'relative',
    width: 'inherit',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '.image-preview': {
      'img, canvas': {
        border: `1px solid ${theme.colors.border.transparent}`,
        borderRadius: theme.shape.borderRadius,
        transition: theme.transitions.out.border,
      },
    },
    '.previous-button, .next-button': {
      fontSize: 'xx-large',
      position: 'absolute',
      height: '100%',
      zIndex: theme.zIndex.toolbox,
    },
    '.previous-button': {left: 0,},
    '.next-button': {right: 0,},
  },
  '.thumnbails-row': {
    justifyContent: 'start',
    marginTop: theme.spacing(2),
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(2),
    '.image-drag-anchor': {
      width: theme.spacing(3),
      height: theme.spacing(3),
      padding: theme.spacing(0, .2),
      margin: theme.spacing(.3),
      cursor: 'grab',
      'svg': {width: 20}
    },
    '.image-thumbnail': {
      'img, canvas': {
        width: 95,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.colors.border.transparent}`,
        transition: theme.transitions.out.border,
        '&:hover': {
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.colors.border.dark}`,
          transition: theme.transitions.in.border,
        }
      }
    },
    '.image-thumbnail.active': {
      'img, canvas': {
        border: `1px solid ${theme.colors.border.dark}`,
        borderRadius: theme.shape.borderRadius,
      }
    },
    '.multi-row-drag-drop': {
      '&.dragging': {
        '.image-drag-anchor': {opacity: 0},
        '.image-thumbnail, .image-thumbnail.active': {
          'img, canvas': {
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.colors.border.transparent}`,
            transition: theme.transitions.out.border,
          }
        }
      },
    }
  },
}));

export const ImageGallery = ({className, hoverForPopup, allowReorderThumbs, clickForFullscreen, images, onReorderThumbs, showPDFPagination, showThumbs}) => {
  const [image, setImage] = useState(images[0] ?? null);
  const [zoomedImage, setZoomedImage] = useState(images[0] ?? null);
  const [isZoomed, setIsZoomed] = useState(false);
  const galleryRef = useRef();

  const handleZoomToggle = useCallback(() => {
    if (!isZoomed) {
      galleryRef.current.focus();
      setZoomedImage(image);
    }
    if (clickForFullscreen) {
      setIsZoomed(!isZoomed);
    }
  }, [clickForFullscreen, image, isZoomed, setIsZoomed, setZoomedImage]);

  useEffect(() => {
    if (!image) {
      setImage(images[0]);
    }
    if (!images.includes(image)) {
      setImage(images[0]);
    }
  }, [images, image]);

  const nextImage = useCallback((e) => {
    e.stopPropagation();
    if (!isZoomed) {
      const imageIndex = images.findIndex((index) => index === image);
      setImage(images[images.length - 1 === imageIndex ? 0 : imageIndex + 1]);
    }
    if (isZoomed) {
      const imageIndex = images.findIndex((index) => index === zoomedImage);
      setZoomedImage(images[images.length - 1 === imageIndex ? 0 : imageIndex + 1]);
    }
  }, [image, images, isZoomed, zoomedImage, setZoomedImage]);

  const previousImage = useCallback((e) => {
    e.stopPropagation();
    if (!isZoomed) {
      const imageIndex = images.findIndex((index) => index === image);
      setImage(images[imageIndex === 0 ? images.length - 1 : imageIndex - 1]);
    }
    if (isZoomed) {
      const imageIndex = images.findIndex((index) => index === zoomedImage);
      setZoomedImage(images[imageIndex === 0 ? images.length - 1 : imageIndex - 1]);
    }
  }, [image, images, isZoomed, zoomedImage, setZoomedImage]);

  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape' && isZoomed) {
      handleZoomToggle();
    }
  }, [isZoomed, handleZoomToggle]);

  const handleDragEnd = useCallback((draggedItem, array) => {
    onReorderThumbs(array.map((thumb) => (thumb.key)));
  }, [onReorderThumbs]);

  const handleSetImage = useCallback((imageUrl) => {
    setImage(imageUrl);
  }, [setImage]);

  return (
    <div className={classNames([className, 'image-gallery'])} ref={galleryRef} tabIndex={0} onKeyDown={handleEscape}>
      {images?.length > 0 &&
        <>
          <div className={'image-container'}>
            {images?.length > 1 &&
              <ScrollLeftIcon className={'previous-button'} onClick={previousImage} />
            }
            <ImagePreview
              className={classNames([clickForFullscreen && 'zoom-cursor'])}
              key={image}
              hoverForPopup={hoverForPopup}
              imageUrl={image}
              onClick={handleZoomToggle}
              setByContainerWidth
              showPDFPagination={showPDFPagination}
            />

            {images?.length > 1 &&
              <ScrollRightIcon className={'next-button'} onClick={nextImage} />
            }
          </div>
          {(showThumbs && allowReorderThumbs) &&
            <Row wrap className={'thumnbails-row'}>
              <MultiRowDragDrop onDragEnd={handleDragEnd} draggable={allowReorderThumbs}>
                {images.map((imageUrl) => (
                  <div className={'hover-tools-container'} key={imageUrl} >
                    <HoverTools className={'image-drag-anchor'} >
                      <DragIndicator />
                    </HoverTools>
                    <ImageThumbnail width={95} hoverForPopup={false} imageUrl={imageUrl} className={classNames([imageUrl === image && 'active'])} onClick={() => handleSetImage(imageUrl)} />
                  </div>
                ))}
              </MultiRowDragDrop>
            </Row>
          }
          {(showThumbs && !allowReorderThumbs) &&
            <Row wrap className={'thumnbails-row'}>
              {images.map((imageUrl) => (
                <ImageThumbnail key={imageUrl} width={95} hoverForPopup={false} imageUrl={imageUrl} className={classNames([imageUrl === image && 'active'])} onClick={() => handleSetImage(imageUrl)} />
              ))}
            </Row>
          }
        </>
      }
      {images?.length === 0 &&
        <>
          <RectangularSkeleton rounded animation={false} width={544} height={544} />
          <Row className={'thumnbails-row'}>
            <RectangularSkeleton className={'thumbnail'} rounded animation={false} width={118} height={118} />
            <RectangularSkeleton className={'thumbnail'} rounded animation={false} width={118} height={118} />
            <RectangularSkeleton className={'thumbnail'} rounded animation={false} width={118} height={118} />
            <RectangularSkeleton className={'thumbnail'} rounded animation={false} width={118} height={118} />
          </Row>
        </>
      }
      {isZoomed && <ImageGalleryOverlay images={images} image={image} onClose={handleZoomToggle} />}
    </div>
  );
};

ImageGallery.propTypes = {
  className: HoopsPropTypes.className,
  allowReorderThumbs: HoopsPropTypes.bool,
  clickForFullscreen: HoopsPropTypes.bool, // enable full screen image view on click
  hoverForPopup: HoopsPropTypes.bool, // enable a scaled up preview of image in popup window on hover
  images: HoopsPropTypes.arrayOfString,
  onReorderThumbs: HoopsPropTypes.func,
  showPDFPagination: HoopsPropTypes.bool,
  showThumbs: HoopsPropTypes.bool,
};
